import React from 'react';
import { Card, Space, Steps, Tag, Typography } from 'antd';
import type { TimelineStage } from './types';
import { labelStopState, stopStateColor } from './executionLabels';

const { Text } = Typography;

type Props = {
  stages: TimelineStage[];
};

function formatTs(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return value;
  }
}

function stageStatus(stage: TimelineStage): 'wait' | 'process' | 'finish' | 'error' {
  if (stage.executionState.includes('FAILED')) return 'error';
  if (stage.completionPercent >= 100) return 'finish';
  if (stage.completionPercent > 0 || stage.executionState.includes('ING') || stage.executionState.includes('ARRIVED')) {
    return 'process';
  }
  return 'wait';
}

export const ExecutionTimeline: React.FC<Props> = ({ stages }) => {
  const current = Math.max(
    0,
    stages.findIndex((s) => s.completionPercent < 100 && !s.executionState.endsWith('ED'))
  );

  return (
    <Card title="Execution timeline" size="small" style={{ marginBottom: 16 }}>
      <Steps
        direction="vertical"
        size="small"
        current={current >= 0 ? current : stages.length - 1}
        items={stages.map((stage) => ({
          title: (
            <Space wrap>
              <Text strong>{stage.label}</Text>
              {stage.kind !== 'transit' ? (
                <Tag color={stopStateColor(stage.executionState)}>{labelStopState(stage.executionState)}</Tag>
              ) : (
                <Tag color="processing">{stage.executionState}</Tag>
              )}
              {stage.isPartial ? <Tag color="warning">Partial</Tag> : null}
            </Space>
          ),
          description: (
            <div>
              <Text type="secondary">{stage.completionPercent}%</Text>
              {stage.blockers.length > 0 ? (
                <div>
                  {stage.blockers.map((b) => (
                    <Tag key={b} color="orange" style={{ marginTop: 4 }}>
                      {b}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {stage.timestamps
                .filter((t) => t.at)
                .map((t) => (
                  <div key={t.label}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t.label}: {formatTs(t.at)}
                    </Text>
                  </div>
                ))}
            </div>
          ),
          status: stageStatus(stage)
        }))}
      />
    </Card>
  );
};

export default ExecutionTimeline;
